/**
 * Configuration › Secrets › File: a secret that is a file, e.g. a Google service account's JSON key (vfs-service
 * src/appconfig/fileSecrets.js). The person drops or picks a file (any file, binary too: sent as base64) or pastes its
 * text; the service gets NAME set to the path of a private, read-only copy of it outside the project, and optionally a
 * second variable with the same path (GOOGLE_APPLICATION_CREDENTIALS) and a fixed path. "Used for": the path in the
 * service folder it stands for, when a compose file bind-mounts a file the project lacks on purpose (a key kept out of
 * git): Flow mounts the secret there instead.
 *
 * Write-only like the other secrets: the content leaves the page once saved and is never shown again. Replacing a file
 * secret keeps its second variable and path unless changed; without a new file only those change.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FILE_SECRET_MAX_BYTES, fileToBase64, formatBytes, mountPathProblem, nameProblem, textBytes } from './appConfigModel';

export default function FileSecretForm({ existing = null, reserved, busy = false, maxBytes = FILE_SECRET_MAX_BYTES, onSave, onCancel }) {
  const [name, setName] = useState(existing?.name || '');
  const [picked, setPicked] = useState(null); // { fileName, size, base64 }
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState(existing?.fileName || '');
  const [envName, setEnvName] = useState(existing?.envName || '');
  const [mountPath, setMountPath] = useState(existing?.mountPath || '');
  const [provides, setProvides] = useState(existing?.provides || '');
  const [problem, setProblem] = useState(null);
  const [dragging, setDragging] = useState(false);
  const limit = formatBytes(maxBytes);

  const take = async(file) => {
    if (!file) { return; }
    if (file.size > maxBytes) { setProblem(`${file.name} is ${formatBytes(file.size)}: a file secret can be at most ${limit}.`); return; }
    if (file.size === 0) { setProblem(`${file.name} is empty.`); return; }
    try {
      const base64 = await fileToBase64(file);
      setPicked({ fileName: file.name, size: file.size, base64 });
      setText('');
      setFileName(file.name);
      setProblem(null);
    } catch (e) {
      setProblem(e.message || 'The file could not be read.');
    }
  };

  const clear = () => { setPicked(null); setText(''); setProblem(null); };

  const save = async(event) => {
    event.preventDefault();
    const n = (existing?.name || name).trim();
    const why = nameProblem(n, reserved);
    if (why) { setProblem(why); return; }
    const alias = envName.trim();
    if (alias) {
      const aliasWhy = nameProblem(alias, reserved);
      if (aliasWhy) { setProblem(`Also set as: ${aliasWhy}`); return; }
      if (alias === n) { setProblem(`${alias} is already the secret's own name.`); return; }
    }
    const pathWhy = mountPathProblem(mountPath);
    if (pathWhy) { setProblem(pathWhy); return; }
    if (text && textBytes(text) > maxBytes) { setProblem(`The pasted content is ${formatBytes(textBytes(text))}: a file secret can be at most ${limit}.`); return; }
    const content = picked ? { content: picked.base64, encoding: 'base64' } : text ? { content: text, encoding: 'utf8' } : null;
    if (!content && !existing) { setProblem('Drop or choose a file, or paste its content.'); return; }
    const usedFor = provides.trim().replace(/^\.\//, '');
    if (usedFor && (usedFor.startsWith('/') || usedFor.split('/').some((part) => part === '..'))) { setProblem('Used for: a path in the service folder, e.g. operations-lab.json.'); return; }
    const ok = await onSave(n, { ...(content || {}), fileName: fileName.trim() || undefined, envName: alias, mountPath: mountPath.trim(), ...(usedFor || existing?.provides ? { provides: usedFor } : {}) });
    // The content leaves the page once saved: it is never shown again.
    setPicked(null);
    setText('');
    if (ok) { onCancel(); }
  };

  return (
    <form className="cfg-file-form" data-testid="cfg-file-secret-form" onSubmit={save}>
      {!existing && (
        <label className="cfg-field">
          <span>Name</span>
          <input aria-label="File secret name" autoComplete="off" className="cfg-input cfg-mono" onChange={(e) => setName(e.target.value.toUpperCase())} placeholder="VERTEX_SERVICE_ACCOUNT" value={name} />
        </label>
      )}
      <label
        className={`cfg-drop${dragging ? ' is-over' : ''}`}
        data-testid="cfg-file-drop"
        onDragLeave={() => setDragging(false)}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDrop={(e) => { e.preventDefault(); setDragging(false); take(e.dataTransfer?.files?.[0]); }}
      >
        <input aria-label="Choose a file" className="cfg-sr" onChange={(e) => { take(e.target.files?.[0]); e.target.value = ''; }} type="file" />
        {picked
          ? <span><strong className="cfg-mono">{picked.fileName}</strong> <span className="cfg-muted">{formatBytes(picked.size)}, ready to save</span></span>
          : <span>Drop a file here or <span className="cfg-linkbtn">choose one</span> <span className="cfg-muted">(any file up to {limit}, e.g. a service account key)</span></span>}
      </label>
      {!picked && (
        <textarea
          aria-label="Or paste the file's content"
          autoComplete="off"
          className="cfg-input cfg-mono cfg-paste"
          onChange={(e) => setText(e.target.value)}
          placeholder={existing ? 'Or paste the new content (leave empty to keep the current file)' : 'Or paste the file\'s content'}
          rows={4}
          spellCheck={false}
          value={text}
        />
      )}
      <div className="cfg-file-fields">
        <label className="cfg-field">
          <span>File name</span>
          <input aria-label="File name" autoComplete="off" className="cfg-input cfg-mono" onChange={(e) => setFileName(e.target.value)} placeholder="service-account.json" value={fileName} />
        </label>
        <label className="cfg-field">
          <span>Also set as <span className="cfg-muted">(optional)</span></span>
          <input aria-label="Also set as" autoComplete="off" className="cfg-input cfg-mono" onChange={(e) => setEnvName(e.target.value.toUpperCase())} placeholder="GOOGLE_APPLICATION_CREDENTIALS" value={envName} />
        </label>
        <label className="cfg-field">
          <span>Fixed path <span className="cfg-muted">(optional, only if the framework needs one)</span></span>
          <input aria-label="Fixed path" autoComplete="off" className="cfg-input cfg-mono" onChange={(e) => setMountPath(e.target.value)} placeholder="/var/secrets/google/key.json" value={mountPath} />
        </label>
        <label className="cfg-field">
          <span>Used for <span className="cfg-muted">(optional: a file a compose file mounts that is not in the project)</span></span>
          <input aria-label="Used for" autoComplete="off" className="cfg-input cfg-mono" onChange={(e) => setProvides(e.target.value)} placeholder="operations-lab.json" value={provides} />
        </label>
      </div>
      <p className="cfg-muted cfg-small">The service gets the file&apos;s path in <code>{(existing?.name || name || 'NAME').trim() || 'NAME'}</code>{envName ? <> and <code>{envName}</code></> : null}: a private, read-only file outside the project, never in git and never shown again.</p>
      <div className="cfg-file-actions">
        <button className="cfg-btn is-sm is-primary" disabled={busy} type="submit">{existing ? 'Replace' : 'Save file secret'}</button>
        {(picked || text) && <button className="cfg-btn is-sm is-quiet" onClick={clear} type="button">Clear</button>}
        <button className="cfg-btn is-sm is-quiet" onClick={onCancel} type="button">Cancel</button>
      </div>
      {problem && <p className="cfg-error" role="alert">{problem}</p>}
    </form>
  );
}

FileSecretForm.propTypes = {
  busy: PropTypes.bool,
  // The file secret being replaced (its row): name, fileName, envName, mountPath. Null: a new one.
  existing: PropTypes.shape({ envName: PropTypes.string, fileName: PropTypes.string, mountPath: PropTypes.string, name: PropTypes.string, provides: PropTypes.string }),
  maxBytes: PropTypes.number,
  onCancel: PropTypes.func.isRequired,
  // (name, { content?, encoding?, fileName, envName, mountPath, provides }) → Promise<boolean> (saved)
  onSave: PropTypes.func.isRequired,
  reserved: PropTypes.shape({ names: PropTypes.arrayOf(PropTypes.string), prefixes: PropTypes.arrayOf(PropTypes.string) }),
};
